import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

type AuthCtx = {
  user: any; loading: boolean; features: Record<string, boolean>; refreshFeatures: () => void;
  setSession: (user: any, token: string) => void;
  refresh: () => Promise<any>;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthCtx>(null as any);
const ALL_ON = { live: true, shop: true, circles: true, reader_signups: true, community: true };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState<Record<string, boolean>>(ALL_ON);
  const refreshFeatures = useCallback(() => { api.get("/settings/public").then((r) => setFeatures({ ...ALL_ON, ...(r.data.features || {}) })).catch(() => {}); }, []);
  useEffect(() => { refreshFeatures(); }, [refreshFeatures]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me when returning from OAuth; AuthCallback exchanges session_id first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  const setSession = (u: any, token: string) => {
    localStorage.setItem("tt_token", token);
    setUser(u);
    setLoading(false);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("tt_token");
    setUser(null);
  };

  return <Ctx.Provider value={{ user, loading, features, refreshFeatures, setSession, refresh, logout }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
